import { z } from 'zod';

/**
 * Browser-extension recorder import contracts (Delivery roadmap
 * Tier 3b.4).
 *
 * The Chrome MV3 extension captures DOM events and POSTs them to
 * `/api/recorder/import`. The API normalises into the flow-engine
 * step shape and inserts a row into `flows`. New flows can then be
 * deep-linked back into flow-builder for editing.
 */

export const RECORDER_PROTOCOL_VERSION = 1;
export const RECORDER_MAX_EVENTS = 5_000;

export const RecorderEventTypeSchema = z.enum([
  'navigate',
  'click',
  'fill',
  'select',
  'check',
  'uncheck',
  'key',
  'submit',
  'wait',
  'assert.text',
  'assert.url',
  'assert.visible',
  'note',
]);
export type RecorderEventType = z.infer<typeof RecorderEventTypeSchema>;

export const RecorderSelectorSchema = z.object({
  /** Canonical CSS selector — first preference. */
  css: z.string().optional(),
  /** ARIA role + name (`button[name="Sign in"]`) — second preference. */
  role: z.string().optional(),
  /** Visible text — third preference (used by self-healer). */
  text: z.string().optional(),
  /** XPath fallback — last resort. */
  xpath: z.string().optional(),
});
export type RecorderSelector = z.infer<typeof RecorderSelectorSchema>;

export const RecorderEventSchema = z.object({
  type: RecorderEventTypeSchema,
  /** Monotonic timestamp in ms since the recording started. */
  timestamp: z.number().int().nonnegative(),
  /** URL active when the event fired. */
  url: z.string().url(),
  /** Selector hint, when applicable. Optional for `navigate` / `note`. */
  selector: RecorderSelectorSchema.optional(),
  /** Free-form value (typed text, key name, expected text, etc). */
  value: z.string().optional(),
  /**
   * When true, the captured value was a password / secret-tagged
   * field; the API stores a placeholder rather than the raw text.
   */
  secret: z.boolean().optional(),
  /** True if the element was inside a cross-origin iframe. */
  crossOriginFrame: z.boolean().optional(),
});
export type RecorderEvent = z.infer<typeof RecorderEventSchema>;

export const RecorderImportPayloadSchema = z.object({
  protocolVersion: z.number().int().min(1).max(RECORDER_PROTOCOL_VERSION),
  /** Suggested name for the flow — frontend lets the user override. */
  name: z.string().min(1).max(120),
  /** Optional project to land the flow into. Defaults to the user's last project. */
  projectId: z.string().uuid().optional(),
  /** Browser + extension metadata for diagnostics. */
  client: z
    .object({
      extensionVersion: z.string(),
      browserName: z.string(),
      browserVersion: z.string().optional(),
    })
    .optional(),
  events: z.array(RecorderEventSchema).min(1).max(RECORDER_MAX_EVENTS),
});
export type RecorderImportPayload = z.infer<typeof RecorderImportPayloadSchema>;

export const RecorderImportResponseSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  redirectUrl: z.string(),
  /** Number of recorder events that were dropped (e.g. cross-origin frame skipped). */
  warningsCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()),
});
export type RecorderImportResponse = z.infer<typeof RecorderImportResponseSchema>;

/**
 * Step-engine shape produced by the normaliser. Matches the
 * existing flow-builder runtime so an imported flow can be edited
 * with no migration step.
 */
export interface RecorderConvertedStep {
  type: string;
  selector?: RecorderSelector;
  value?: string;
  url?: string;
  /** Pre-resolved label for the step list. */
  label: string;
  meta?: Record<string, unknown>;
}

/**
 * Convert a single recorder event into a step-engine step. Pure —
 * exported so the API service and the extension-side preview can
 * use the same translation table.
 */
export function recorderEventToStep(evt: RecorderEvent): RecorderConvertedStep | null {
  switch (evt.type) {
    case 'navigate':
      return { type: 'navigate', url: evt.url, label: `Go to ${evt.url}` };
    case 'click':
      return {
        type: 'click',
        selector: evt.selector,
        label: `Click ${describeSelector(evt.selector)}`,
      };
    case 'fill':
      return {
        type: 'fill',
        selector: evt.selector,
        value: evt.secret ? '${SECRET}' : (evt.value ?? ''),
        label: `Fill ${describeSelector(evt.selector)}${evt.secret ? ' (secret)' : ''}`,
      };
    case 'select':
      return {
        type: 'select',
        selector: evt.selector,
        value: evt.value ?? '',
        label: `Select ${evt.value} in ${describeSelector(evt.selector)}`,
      };
    case 'check':
      return { type: 'check', selector: evt.selector, label: `Check ${describeSelector(evt.selector)}` };
    case 'uncheck':
      return { type: 'uncheck', selector: evt.selector, label: `Uncheck ${describeSelector(evt.selector)}` };
    case 'key':
      return {
        type: 'press',
        selector: evt.selector,
        value: evt.value ?? '',
        label: `Press ${evt.value}`,
      };
    case 'submit':
      return { type: 'submit', selector: evt.selector, label: `Submit form` };
    case 'wait':
      return { type: 'wait', value: evt.value ?? '500', label: `Wait ${evt.value ?? '500'} ms` };
    case 'assert.text':
      return {
        type: 'assertText',
        selector: evt.selector,
        value: evt.value ?? '',
        label: `Assert text "${evt.value}"`,
      };
    case 'assert.url':
      return { type: 'assertUrl', value: evt.value ?? evt.url, label: `Assert URL is ${evt.value ?? evt.url}` };
    case 'assert.visible':
      return {
        type: 'assertVisible',
        selector: evt.selector,
        label: `Assert visible: ${describeSelector(evt.selector)}`,
      };
    case 'note':
      return null; // notes survive in metadata only
  }
}

function describeSelector(sel?: RecorderSelector): string {
  if (!sel) return 'element';
  if (sel.role) return sel.role;
  if (sel.text) return `text "${sel.text}"`;
  if (sel.css) return sel.css;
  return sel.xpath ?? 'element';
}

/**
 * Top-level normaliser: events → steps, with a warnings list for
 * any events the runner can't honour (cross-origin iframes today,
 * unrecognised types in the future).
 */
export function normaliseRecorderPayload(payload: RecorderImportPayload): {
  steps: RecorderConvertedStep[];
  warnings: string[];
} {
  const steps: RecorderConvertedStep[] = [];
  const warnings: string[] = [];
  for (const evt of payload.events) {
    if (evt.crossOriginFrame) {
      warnings.push(
        `Skipped event in cross-origin frame at ${evt.url} (${evt.type}). Re-record with the frame whitelisted.`,
      );
      continue;
    }
    const step = recorderEventToStep(evt);
    if (!step) continue;
    steps.push(step);
  }
  return { steps, warnings };
}
